import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { CHAT } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STARTERS = [
  "Which service is right for my brand?",
  "How does SEO work?",
  "What's typical PPC budget?",
  "Can you help with my website?",
];

export const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hey, I'm Akshay's AI assistant 👋  Ask me anything about SEO, PPC, lead gen or websites.",
    },
  ]);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const sendMessage = async (text) => {
    const messageToSend = (text ?? input).trim();
    if (!messageToSend || streaming) return;
    setInput("");

    setMessages((m) => [...m, { role: "user", content: messageToSend }]);
    // placeholder assistant message we'll stream into
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: messageToSend }),
      });

      if (!resp.ok || !resp.body) {
        throw new Error("Network response failed");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const json = line.replace(/^data:\s*/, "").trim();
          if (!json) continue;
          try {
            const evt = JSON.parse(json);
            if (evt.type === "session") {
              setSessionId(evt.session_id);
            } else if (evt.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = {
                    ...last,
                    content: (last.content || "") + evt.content,
                  };
                }
                return copy;
              });
            } else if (evt.type === "done") {
              // streaming finished
            } else if (evt.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content:
                    "Sorry, something went wrong. Please email hello@akshaysalve.com",
                };
                return copy;
              });
            }
          } catch (_) {
            // ignore parse errors
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "Couldn't reach the AI right now. Email hello@akshaysalve.com and Akshay will get back to you.",
          };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Trigger */}
      <motion.button
        data-testid={CHAT.trigger}
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className={`fixed bottom-24 right-6 z-50 inline-flex items-center gap-3 px-5 py-4 bg-[#002FA7] text-white shadow-2xl shadow-[#002FA7]/30 hover:bg-[#00227A] hover:-translate-y-1 transition-all ${
          open ? "opacity-0 pointer-events-none" : ""
        }`}
      >
        <MessageSquare className="h-5 w-5" />
        <span className="text-sm font-bold uppercase tracking-widest">Ask AI</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={CHAT.dialog}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 right-6 z-40 w-[calc(100%-3rem)] sm:w-[420px] h-[600px] max-h-[80vh] bg-white border border-zinc-200 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-[#002FA7] text-white">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 bg-white/15 inline-flex items-center justify-center">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-bold uppercase tracking-widest">
                    Ask Akshay&rsquo;s AI
                  </div>
                  <div className="text-[10px] text-white/70 uppercase tracking-widest">
                    Powered by Claude · Instant replies
                  </div>
                </div>
              </div>
              <button
                data-testid={CHAT.close}
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="p-1 hover:bg-white/15"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid={CHAT.messages}
              className="flex-1 overflow-y-auto chat-scroll px-5 py-5 space-y-4 bg-white"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-zinc-900 text-white"
                        : "bg-[#F4F4F5] text-zinc-900 border border-zinc-200"
                    }`}
                  >
                    {m.content ||
                      (streaming && i === messages.length - 1 ? (
                        <span className="cursor-blink" />
                      ) : (
                        ""
                      ))}
                    {streaming &&
                      i === messages.length - 1 &&
                      m.role === "assistant" &&
                      m.content && <span className="cursor-blink" />}
                  </div>
                </div>
              ))}

              {/* Suggested starters */}
              {messages.length <= 1 && !streaming && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      className="text-xs px-3 py-2 border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] text-zinc-700 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              className="flex items-stretch border-t border-zinc-200 bg-white"
            >
              <input
                data-testid={CHAT.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about SEO, PPC, leads…"
                disabled={streaming}
                className="flex-1 px-4 py-4 text-sm bg-transparent placeholder:text-zinc-400 focus:outline-none disabled:opacity-60"
              />
              <button
                data-testid={CHAT.send}
                type="submit"
                disabled={streaming || !input.trim()}
                className="px-5 bg-[#002FA7] text-white disabled:opacity-50 hover:bg-[#00227A] transition-colors"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
